
import React, { useEffect, useState } from 'react';
import { Card, Button, Badge, Input, Select } from '../components/UI';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { Activity, Clock, MessageSquare, RefreshCw, AlertTriangle, CheckCircle, Users, Terminal, Edit2, Save, X, Palette, FileText, Send, Calendar, Globe, Trash2, GitMerge, Link as LinkIcon, Radio, Play, Wand2, CheckSquare, Square, Info, BellRing, Trophy, Zap } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { apiRequest } from '../utils/api';

// ... (Existing Interfaces) ...
interface DiscordLog {
    id: number;
    title: string;
    message: string;
    target: string;
    status: string;
    created_at: string;
}

interface ForecastItem {
    type: string;
    event_id: string; // NEW
    event: string;
    trigger_at: string;
    hours_until?: number;
    status: 'PENDING' | 'DUE_NOW';
    missing_count?: number;
    missing_drivers: { username: string; discord_id: string | null }[];
}

interface MessageTemplate {
    id: number;
    template_key: string;
    title: string;
    message: string;
    color: string;
    description: string;
    placeholders: string;
}

interface ScheduledMessage {
    id: number;
    title: string;
    message: string;
    color: string;
    webhook_url: string;
    scheduled_at: string;
    status: 'PENDING' | 'SENT' | 'FAILED';
    pings: string;
}

interface DiscordChannel {
    channel_key: string;
    label: string;
    description: string;
    webhook_url: string | null;
    pings: string | null;
    use_global: boolean;
}

// COMPONENT: Manual Trigger
const ManualTrigger: React.FC = () => {
    const [events, setEvents] = useState<any[]>([]);
    const [channels, setChannels] = useState<DiscordChannel[]>([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [selectedWebhook, setSelectedWebhook] = useState('');
    const [scheduledAt, setScheduledAt] = useState('');
    const [loading, setLoading] = useState(false);
    const [triggering, setTriggering] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            apiRequest<any[]>('events.php?scope=recent').catch(() => []),
            apiRequest<DiscordChannel[]>('discord_config.php').catch(() => [])
        ]).then(([eventData, channelData]) => {
            const sorted = (Array.isArray(eventData) ? eventData : []).sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
            setEvents(sorted);
            // Auto-select next upcoming event, or most recent
            const upcoming = sorted.find((e: any) => e.status !== 'COMPLETED');
            if (upcoming) setSelectedEventId(upcoming.id);
            else if (sorted.length > 0) setSelectedEventId(sorted[0].id);
            
            setChannels(Array.isArray(channelData) ? channelData : []);
            setLoading(false);
        });
    }, []);

    const handleTrigger = async (type: string, label: string) => {
        if (!selectedEventId) return;
        
        const isScheduled = scheduledAt && new Date(scheduledAt).getTime() > Date.now();
        const confirmMsg = isScheduled 
            ? `Möchtest du "${label}" für den ${new Date(scheduledAt).toLocaleString()} einplanen?`
            : `Möchtest du "${label}" jetzt sofort senden?`;

        if (!confirm(confirmMsg)) return;
        
        setTriggering(type);
        try {
            if (type === 'DOTD_FINISH') {
                const res = await apiRequest<{message: string}>('vote.php', 'POST', {
                    action: 'force_finish',
                    eventId: selectedEventId
                });
                alert(res.message);
            } else {
                const res = await apiRequest<{message: string}>('discord_monitor.php', 'POST', {
                    action: 'force_trigger',
                    eventId: selectedEventId,
                    type,
                    scheduledAt: scheduledAt || null,
                    webhookUrl: selectedWebhook || null
                });
                alert(res.message);
            }
        } catch(e: any) {
            alert("Fehler: " + e.message);
        } finally {
            setTriggering(null);
        }
    };

    const selectedEvent = events.find(e => e.id === selectedEventId);

    return (
        <Card title="Manuelle Auslösung">
            <div className="space-y-6">
                <div className="bg-blue-900/20 border border-blue-800 p-4 rounded text-sm text-blue-200">
                    Hier kannst du Standard-Nachrichten manuell auslösen oder für später einplanen.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Event wählen</label>
                        <select 
                            className="w-full bg-gray-900 border border-gray-700 rounded p-3 text-white text-sm"
                            value={selectedEventId}
                            onChange={e => setSelectedEventId(e.target.value)}
                        >
                            {events.map(e => (
                                <option key={e.id} value={e.id}>
                                    {e.date} - {e.trackLocation} (R{e.roundNumber})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Ziel-Kanal (Optional)</label>
                        <select 
                            className="w-full bg-gray-900 border border-gray-700 rounded p-3 text-white text-sm"
                            value={selectedWebhook}
                            onChange={e => setSelectedWebhook(e.target.value)}
                        >
                            <option value="">-- Standard (Routing) --</option>
                            {channels.filter(c => c.webhook_url).map(c => (
                                <option key={c.channel_key} value={c.webhook_url!}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Zeitpunkt (Optional)</label>
                        <input 
                            type="datetime-local" 
                            className="w-full bg-gray-900 border border-gray-700 rounded p-3 text-white text-sm"
                            value={scheduledAt}
                            onChange={e => setScheduledAt(e.target.value)}
                        />
                    </div>
                </div>

                {selectedEvent && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-gray-900 rounded border border-gray-700">
                            <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Calendar size={16}/> Vor dem Rennen</h3>
                            <div className="space-y-2">
                                <Button 
                                    className="w-full justify-start" 
                                    onClick={() => handleTrigger('ATTENDANCE', 'Anmeldung Erinnerung')}
                                    disabled={!!triggering}
                                >
                                    {triggering === 'ATTENDANCE' ? <RefreshCw className="animate-spin mr-2"/> : <BellRing size={16} className="mr-2 text-yellow-500"/>}
                                    {scheduledAt ? 'Anmeldung Erinnerung einplanen' : 'Anmeldung Erinnerung senden'}
                                </Button>
                                <Button 
                                    className="w-full justify-start" 
                                    onClick={() => handleTrigger('PREDICTION', 'Tippspiel Erinnerung')}
                                    disabled={!!triggering}
                                >
                                    {triggering === 'PREDICTION' ? <RefreshCw className="animate-spin mr-2"/> : <BellRing size={16} className="mr-2 text-purple-500"/>}
                                    {scheduledAt ? 'Tippspiel Erinnerung einplanen' : 'Tippspiel Erinnerung senden'}
                                </Button>
                            </div>
                        </div>

                        <div className="p-4 bg-gray-900 rounded border border-gray-700">
                            <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Zap size={16}/> Nach dem Rennen</h3>
                            <div className="space-y-2">
                                <Button 
                                    className="w-full justify-start" 
                                    onClick={() => handleTrigger('DOTD_OPEN', 'Voting Starten (DotD)')}
                                    disabled={!!triggering}
                                >
                                    {triggering === 'DOTD_OPEN' ? <RefreshCw className="animate-spin mr-2"/> : <Play size={16} className="mr-2 text-green-500"/>}
                                    {scheduledAt ? 'Voting Start einplanen' : 'Voting Starten (DotD)'}
                                </Button>
                                <Button 
                                    className="w-full justify-start" 
                                    onClick={() => handleTrigger('DOTD_FINISH', 'Voting Beenden & Auswerten')}
                                    disabled={!!triggering}
                                >
                                    {triggering === 'DOTD_FINISH' ? <RefreshCw className="animate-spin mr-2"/> : <CheckCircle size={16} className="mr-2 text-red-500"/>}
                                    Voting Beenden (Sofort)
                                </Button>
                                <Button 
                                    className="w-full justify-start" 
                                    onClick={() => handleTrigger('PREDICTION_RESULTS', 'Tippspiel Ergebnisse')}
                                    disabled={!!triggering}
                                >
                                    {triggering === 'PREDICTION_RESULTS' ? <RefreshCw className="animate-spin mr-2"/> : <Trophy size={16} className="mr-2 text-yellow-400"/>}
                                    {scheduledAt ? 'Tippspiel Ergebnisse einplanen' : 'Tippspiel Ergebnisse senden'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};

// COMPONENT: Template Editor (Unchanged)
const TemplateEditor: React.FC<{ template: MessageTemplate, onSave: (t: MessageTemplate) => void, onCancel: () => void }> = ({ template, onSave, onCancel }) => {
    const [data, setData] = useState(template);
    const [saving, setSaving] = useState(false);
    const handleSave = async () => {
        setSaving(true);
        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            await fetch(`${baseUrl}/discord_templates.php`, { method: 'PUT', body: JSON.stringify(data) });
            onSave(data);
        } catch (e) { alert("Fehler beim Speichern"); } finally { setSaving(false); }
    };
    return (
        <Card className="bg-gray-800 border-gray-600">
            <div className="flex justify-between items-start mb-4 border-b border-gray-700 pb-2">
                <div><h3 className="text-xl font-bold text-white">{data.template_key}</h3><p className="text-sm text-gray-400">{data.description}</p></div>
                <button onClick={onCancel} className="text-gray-400 hover:text-white"><X/></button>
            </div>
            <div className="space-y-4">
                <Input label="Titel" value={data.title} onChange={e => setData({...data, title: e.target.value})}/>
                <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-gray-400 text-xs font-bold mb-2">Farbe</label><input type="color" value={data.color} onChange={e => setData({...data, color: e.target.value})} className="h-10 w-full"/></div>
                    <div><label className="block text-gray-400 text-xs font-bold mb-2">Vars</label><div className="flex flex-wrap gap-1">{data.placeholders.split(',').map(p=><span key={p} className="text-[9px] bg-black px-1 rounded">{'{'+p.trim()+'}'}</span>)}</div></div>
                </div>
                <textarea className="w-full bg-gray-900 border border-gray-700 rounded p-3 text-white h-40 text-sm" value={data.message} onChange={e => setData({...data, message: e.target.value})}/>
                <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onCancel}>Abbrechen</Button><Button onClick={handleSave} disabled={saving}>Speichern</Button></div>
            </div>
        </Card>
    );
};

// COMPONENT: Send Template Modal (Unchanged)
const SendTemplateModal: React.FC<{ template: MessageTemplate, onClose: () => void }> = ({ template, onClose }) => {
    const { teams, drivers } = useLeague();
    const [placeholders, setPlaceholders] = useState<Record<string, string>>({});
    const [scheduleTime, setScheduleTime] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [loadingContext, setLoadingContext] = useState(false);
    const [contextInfo, setContextInfo] = useState<string>(''); 
    const [debugInfo, setDebugInfo] = useState<string>('');
    
    const [events, setEvents] = useState<any[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<string>('');

    const [showMissingSelector, setShowMissingSelector] = useState(false);
    const [missingType, setMissingType] = useState<'ATTENDANCE' | 'PREDICTION'>('ATTENDANCE');
    const [missingDrivers, setMissingDrivers] = useState<{id: string, username: string, discord_id: string|null}[]>([]);
    const [selectedMissingIds, setSelectedMissingIds] = useState<string[]>([]);
    
    const [targetType, setTargetType] = useState<'ALL' | 'TEAM' | 'DRIVER'>('ALL');
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedDrivers, setSelectedDrivers] = useState<string[]>([]);

    useEffect(() => {
        apiRequest<any[]>('events.php?scope=recent').catch(() => []).then(data => {
            const all = Array.isArray(data) ? data : [];
            const sorted = all.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
            setEvents(sorted);
            const upcoming = sorted.find((e: any) => e.status !== 'COMPLETED');
            if(upcoming) setSelectedEventId(upcoming.id);
            else if(sorted.length > 0) setSelectedEventId(sorted[sorted.length - 1].id);
        });
    }, []);

    useEffect(() => {
        const vars = template.placeholders.split(',').map(s => s.trim()).filter(s => s);
        const init: Record<string, string> = {};
        vars.forEach(v => init[v] = '');
        setPlaceholders(init);

        const needsMissing = vars.includes('missing_list');
        if (needsMissing) {
            setShowMissingSelector(true);
            if (template.template_key.includes('PREDICTION')) setMissingType('PREDICTION');
            else setMissingType('ATTENDANCE');
        }

        // Only load context once we have an event selected
        if (!selectedEventId) {
            setContextInfo('Event auswählen...');
            return;
        }
        const initialType = template.template_key.includes('PREDICTION') ? 'PREDICTION' : 'ATTENDANCE';
        loadContext(vars, initialType, selectedEventId);
    }, [template, selectedEventId]);

    const handleTypeChange = (newType: 'ATTENDANCE' | 'PREDICTION') => {
        setMissingType(newType);
        const vars = template.placeholders.split(',').map(s => s.trim()).filter(s => s);
        loadContext(vars, newType, selectedEventId);
    };

    const loadContext = async (vars: string[], type: string, evtId: string) => {
        if (!evtId) return;
        setLoadingContext(true);
        setContextInfo('Lade...');
        try {
            let url = `discord_monitor.php?mode=context&type=${type}&eventId=${evtId}`;
            
            const res = await apiRequest<any>(url);
            if (res && res.data) {
                const ctx = res.data;
                setContextInfo(ctx.event_debug || 'Unbekanntes Event');
                if (ctx.system_debug) setDebugInfo(ctx.system_debug);
                
                if(!evtId && ctx.event_id) setSelectedEventId(ctx.event_id);

                const newPlaceholders = {
                    ...placeholders,
                    ...(vars.includes('track') ? { track: ctx.track } : {}),
                    ...(vars.includes('time_left') ? { time_left: ctx.time_left } : {}),
                    ...(vars.includes('date') ? { date: ctx.date } : {}),
                };

                if (ctx.missing_drivers_raw && Array.isArray(ctx.missing_drivers_raw)) {
                    setMissingDrivers(ctx.missing_drivers_raw);
                    const allIds = ctx.missing_drivers_raw.map((d:any) => d.id);
                    setSelectedMissingIds(allIds);
                    newPlaceholders['missing_list'] = generateMissingString(ctx.missing_drivers_raw, allIds);
                } else {
                    setMissingDrivers([]); 
                    if (vars.includes('missing_list')) newPlaceholders['missing_list'] = ctx.missing_list || "Alle haben erledigt!";
                }
                
                setPlaceholders(newPlaceholders);
            }
        } catch (e) {
            setContextInfo("Fehler beim Laden.");
        } finally {
            setLoadingContext(false);
        }
    };

    const generateMissingString = (drivers: any[], selectedIds: string[]) => {
        const selected = drivers.filter(d => selectedIds.includes(d.id));
        if (selected.length === 0) return "Alle haben erledigt! 🎉";
        const parts = selected.map(d => d.discord_id ? `<@${d.discord_id}>` : d.username);
        return parts.join(", ");
    };

    const toggleMissingDriver = (id: string) => {
        let newSelection;
        if (selectedMissingIds.includes(id)) newSelection = selectedMissingIds.filter(x => x !== id);
        else newSelection = [...selectedMissingIds, id];
        setSelectedMissingIds(newSelection);
        setPlaceholders(prev => ({ ...prev, missing_list: generateMissingString(missingDrivers, newSelection) }));
    };
    
    const toggleSelectAllMissing = () => {
        if (selectedMissingIds.length === missingDrivers.length) {
            setSelectedMissingIds([]);
            setPlaceholders(prev => ({...prev, missing_list: "Alle haben erledigt! 🎉"}));
        } else {
            const all = missingDrivers.map(d => d.id);
            setSelectedMissingIds(all);
            setPlaceholders(prev => ({...prev, missing_list: generateMissingString(missingDrivers, all)}));
        }
    };

    const handleSend = async () => {
        setIsSending(true);
        try {
            let finalTitle = template.title;
            let finalMsg = template.message;
            Object.entries(placeholders).forEach(([key, val]) => {
                finalTitle = finalTitle.replace(new RegExp(`{${key}}`, 'g'), val);
                finalMsg = finalMsg.replace(new RegExp(`{${key}}`, 'g'), val);
            });
            await apiRequest('discord_custom.php', 'POST', {
                title: finalTitle, message: finalMsg, color: template.color, scheduledAt: scheduleTime || null, targetType: targetType, targetId: selectedTeam, targetIds: selectedDrivers
            });
            alert(scheduleTime ? "Nachricht eingeplant!" : "Nachricht gesendet!");
            onClose();
        } catch(e: any) { alert("Fehler: " + e.message); } finally { setIsSending(false); }
    };
    
    const toggleDriver = (id: string) => {
        if (selectedDrivers.includes(id)) setSelectedDrivers(prev => prev.filter(x => x !== id));
        else setSelectedDrivers(prev => [...prev, id]);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg bg-[#1a1a20] border-gray-600 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4 border-b border-gray-700 pb-2">
                    <h3 className="text-xl font-bold uppercase text-white flex items-center gap-2">
                        <Send size={18} className="text-f1-red"/> Senden: {template.template_key}
                    </h3>
                    <button onClick={onClose}><X className="text-gray-400 hover:text-white"/></button>
                </div>
                
                <div className="space-y-4">
                    {/* Event Selector - always visible */}
                    <div className="bg-[#0e0e16] border border-gray-800/40 p-3 rounded-lg">
                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1.5 flex items-center gap-1"><Calendar size={12}/> Event auswählen</label>
                        <select className="w-full bg-[#12121a] border border-gray-800/40 rounded-lg p-2.5 text-white text-sm font-bold outline-none focus:border-f1-red" value={selectedEventId} onChange={e => setSelectedEventId(e.target.value)}>
                            <option value="">-- Automatisch (Nächstes) --</option>
                            {events.map(ev => (<option key={ev.id} value={ev.id}>{ev.trackLocation} (R{ev.roundNumber}) {ev.status === 'COMPLETED' ? '✓' : ''}</option>))}
                        </select>
                    </div>

                    <div className="bg-blue-900/20 p-3 rounded text-xs text-blue-200 border border-blue-800">
                        <div className="flex justify-between items-center mb-1">
                            <span className="flex items-center gap-1 font-bold"><Info size={12}/> Kontext: {contextInfo || 'Lade...'}</span>
                            {loadingContext && <span className="flex items-center gap-1 text-blue-300"><RefreshCw className="animate-spin" size={10}/></span>}
                        </div>
                        {debugInfo && <div className="text-[10px] font-mono text-blue-400 border-t border-blue-800/50 pt-1 mt-1">{debugInfo}</div>}
                    </div>

                    {showMissingSelector && (
                        <div className="bg-gray-900 border border-gray-700 rounded p-3">
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-[10px] uppercase font-bold text-gray-400 flex items-center gap-1"><Users size={12}/> Fehlende Fahrer ({missingDrivers.length})</label>
                                <div className="flex gap-1">
                                    <button onClick={() => handleTypeChange('ATTENDANCE')} className={`px-2 py-0.5 rounded text-[10px] uppercase border ${missingType==='ATTENDANCE'?'bg-f1-red border-f1-red text-white':'bg-gray-800 border-gray-600 text-gray-400'}`}>Anmeldung</button>
                                    <button onClick={() => handleTypeChange('PREDICTION')} className={`px-2 py-0.5 rounded text-[10px] uppercase border ${missingType==='PREDICTION'?'bg-purple-600 border-purple-600 text-white':'bg-gray-800 border-gray-600 text-gray-400'}`}>Tippspiel</button>
                                </div>
                            </div>
                            <div className="max-h-32 overflow-y-auto custom-scrollbar border border-gray-800 rounded bg-black/20 p-2 mb-2">
                                {missingDrivers.length > 0 ? (
                                    <>
                                        <div onClick={toggleSelectAllMissing} className="flex items-center gap-2 cursor-pointer pb-2 border-b border-gray-800 mb-2">
                                            {selectedMissingIds.length === missingDrivers.length ? <CheckSquare size={14} className="text-f1-red"/> : <Square size={14} className="text-gray-500"/>}
                                            <span className="text-xs font-bold text-white italic">Alle Auswählen</span>
                                        </div>
                                        {missingDrivers.map(d => {
                                            const isChecked = selectedMissingIds.includes(d.id);
                                            return (
                                                <div key={d.id} onClick={() => toggleMissingDriver(d.id)} className="flex items-center gap-2 cursor-pointer py-1 hover:bg-gray-800/50 rounded">
                                                    {isChecked ? <CheckSquare size={14} className="text-green-500"/> : <Square size={14} className="text-gray-600"/>}
                                                    <span className={`text-xs ${isChecked ? 'text-gray-300' : 'text-gray-600'}`}>{d.username}</span>
                                                    {d.discord_id && <span className="text-[9px] text-blue-400 bg-blue-900/20 px-1 rounded">@</span>}
                                                </div>
                                            )
                                        })}
                                    </>
                                ) : ( <div className="text-center text-xs text-gray-500 italic py-2">Liste leer (Alle haben reagiert).</div> )}
                            </div>
                        </div>
                    )}

                    {Object.keys(placeholders).length > 0 && (
                        <div className="space-y-3 bg-gray-900 p-3 rounded border border-gray-700">
                            {Object.keys(placeholders).map(key => (
                                <div key={key}>
                                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 flex items-center gap-1">
                                        {key}
                                        {(key === 'track' || key === 'missing_list' || key === 'time_left') && <Wand2 size={10} className="text-purple-400"/>}
                                    </label>
                                    {key === 'missing_list' ? (
                                        <textarea className="w-full bg-gray-800 border border-gray-600 rounded p-2 text-white text-xs focus:border-f1-red outline-none h-20 font-mono" value={placeholders[key]} onChange={e => setPlaceholders({...placeholders, [key]: e.target.value})} />
                                    ) : (
                                        <input className="w-full bg-gray-800 border border-gray-600 rounded p-2 text-white text-sm focus:border-f1-red outline-none" value={placeholders[key]} onChange={e => setPlaceholders({...placeholders, [key]: e.target.value})} />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <div className="border-t border-gray-700 pt-4">
                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-2">Ping Ziel</label>
                        <div className="flex gap-2 mb-2">
                             {['ALL', 'TEAM', 'DRIVER'].map(t => (
                                 <button key={t} onClick={() => setTargetType(t as any)} className={`px-3 py-1 rounded text-xs border ${targetType === t ? 'bg-f1-red text-white' : 'bg-gray-900 text-gray-500'}`}>{t}</button>
                             ))}
                        </div>
                        {targetType === 'TEAM' && <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-sm" onChange={e => setSelectedTeam(e.target.value)}><option>Wähle Team...</option>{teams.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}</select>}
                        {targetType === 'DRIVER' && <div className="max-h-32 overflow-y-auto bg-gray-900 border border-gray-700 rounded p-2 grid grid-cols-2 gap-1 custom-scrollbar">{drivers.map(d=><div key={d.id} onClick={()=>toggleDriver(d.id)} className={`cursor-pointer px-2 py-1 text-xs rounded ${selectedDrivers.includes(d.id)?'bg-green-900 text-green-400':'text-gray-400'}`}>{d.username}</div>)}</div>}
                    </div>
                    
                    <div>
                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Zeitpunkt (Optional)</label>
                        <input type="datetime-local" className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-sm" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} />
                        <p className="text-[10px] text-gray-500 mt-1">Leer lassen für sofortigen Versand.</p>
                    </div>

                    <div className="flex gap-2 pt-2 border-t border-gray-700 mt-4">
                        <Button variant="ghost" onClick={onClose} className="w-1/3">Abbrechen</Button>
                        <Button onClick={handleSend} disabled={isSending} className="w-2/3 justify-center">
                            {isSending ? <RefreshCw className="animate-spin"/> : scheduleTime ? <Calendar size={16} className="mr-2"/> : <Send size={16} className="mr-2"/>}
                            {scheduleTime ? 'Einplanen' : 'Jetzt Senden'}
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};

// ... (ChannelRouting and CustomMessageBuilder Unchanged) ...
const ChannelRouting: React.FC = () => {
    const [channels, setChannels] = useState<DiscordChannel[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadChannels();
    }, []);

    const loadChannels = async () => {
        setLoading(true);
        try {
            const res = await apiRequest<DiscordChannel[]>('discord_config.php');
            setChannels(Array.isArray(res) ? res : []);
        } catch (e) { console.error(e); } finally { setLoading(false); }
    };

    const handleSave = async (channel: DiscordChannel) => {
        try {
            await apiRequest('discord_config.php', 'PUT', channel);
            alert("Gespeichert!");
        } catch(e: any) { alert(e.message); }
    };

    const updateChannel = (idx: number, field: keyof DiscordChannel, value: any) => {
        const newChannels = [...channels];
        newChannels[idx] = { ...newChannels[idx], [field]: value };
        setChannels(newChannels);
    };
    
    const testChannel = async (c: DiscordChannel) => {
        try {
            await apiRequest('discord_config.php?action=test', 'POST', c);
            alert("Test-Nachricht gesendet!");
        } catch(e: any) { alert(e.message); }
    };

    return (
        <Card title="Kanal Routing">
            <div className="space-y-4">
                <div className="text-sm text-gray-400 mb-4 bg-blue-900/20 p-3 rounded border border-blue-800">
                    Definiere hier spezifische Webhooks für verschiedene Ereignisse. Wenn "Global" aktiviert ist, wird der Haupt-Webhook aus den Einstellungen verwendet.
                </div>
                {channels.length === 0 && !loading && (
                    <div className="text-center p-4 text-gray-500 border border-dashed border-gray-700 rounded">
                        Keine Kanäle gefunden. Lade neu, um Standards wiederherzustellen.
                        <Button size="sm" onClick={loadChannels} className="mt-2 block mx-auto" icon={RefreshCw}>Fix Channels</Button>
                    </div>
                )}
                {channels.map((ch, idx) => (
                    <div key={ch.channel_key} className="bg-gray-900 border border-gray-700 rounded p-4 flex flex-col md:flex-row gap-4 items-start md:items-center">
                        <div className="w-1/3">
                            <div className="font-bold text-white">{ch.label}</div>
                            <div className="text-xs text-gray-500">{ch.description}</div>
                        </div>
                        <div className="flex-1 w-full">
                            <div className="flex items-center justify-between mb-2">
                                <label className="flex items-center gap-2 cursor-pointer text-xs uppercase font-bold text-gray-400">
                                    <input type="checkbox" checked={ch.use_global} onChange={e => updateChannel(idx, 'use_global', e.target.checked)} className="rounded bg-gray-800 border-gray-600 text-f1-red focus:ring-0" />
                                    Nutze Global Webhook
                                </label>
                                <button onClick={() => testChannel(ch)} className="text-xs text-blue-400 hover:underline">Testen</button>
                            </div>
                            <input className="w-full bg-gray-800 border border-gray-600 rounded p-2 text-sm text-white focus:border-f1-red outline-none disabled:opacity-50" placeholder="https://discord.com/api/webhooks/..." value={ch.webhook_url || ''} onChange={e => updateChannel(idx, 'webhook_url', e.target.value)} disabled={false} />
                            <div className="mt-2">
                                <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Pings (z.B. @everyone oder {'<@&ROLE_ID>'})</label>
                                <input className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-xs text-gray-300 focus:border-f1-red outline-none" placeholder="@everyone, <@&123456...>" value={ch.pings || ''} onChange={e => updateChannel(idx, 'pings', e.target.value)} />
                            </div>
                        </div>
                        <Button size="sm" onClick={() => handleSave(ch)}>Speichern</Button>
                    </div>
                ))}
            </div>
        </Card>
    );
};

const CustomMessageBuilder: React.FC = () => {
    const { teams, drivers } = useLeague();
    const [msgData, setMsgData] = useState({ title: '', message: '', color: '#e10600', url: '' });
    const [targetType, setTargetType] = useState<'ALL' | 'TEAM' | 'DRIVER'>('ALL');
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedDrivers, setSelectedDrivers] = useState<string[]>([]);
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
        if (!msgData.title || !msgData.message) return;
        setSending(true);
        try {
            await apiRequest('discord_custom.php', 'POST', { ...msgData, targetType, targetId: selectedTeam, targetIds: selectedDrivers });
            alert("Nachricht gesendet!");
            setMsgData({ title: '', message: '', color: '#e10600', url: '' });
        } catch (e: any) { alert(e.message); } finally { setSending(false); }
    };

    const toggleDriver = (id: string) => {
        if (selectedDrivers.includes(id)) setSelectedDrivers(prev => prev.filter(x => x !== id));
        else setSelectedDrivers(prev => [...prev, id]);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card title="Nachricht verfassen">
                <div className="space-y-4">
                    <Input label="Titel" value={msgData.title} onChange={e => setMsgData({...msgData, title: e.target.value})} placeholder="Wichtige Info..."/>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Farbe</label><input type="color" className="w-full h-10 bg-transparent cursor-pointer" value={msgData.color} onChange={e => setMsgData({...msgData, color: e.target.value})}/></div>
                        <Input label="Link URL (Optional)" value={msgData.url} onChange={e => setMsgData({...msgData, url: e.target.value})} placeholder="https://..."/>
                    </div>
                    <div><label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Nachricht</label><textarea className="w-full bg-gray-900 border border-gray-700 rounded p-3 text-white h-32 focus:border-f1-red outline-none text-sm" value={msgData.message} onChange={e => setMsgData({...msgData, message: e.target.value})} placeholder="Inhalt..."/></div>
                    <div className="border-t border-gray-700 pt-4"><label className="block text-[10px] uppercase font-bold text-gray-500 mb-2">Empfänger (Ping)</label><div className="flex gap-2 mb-2">{['ALL', 'TEAM', 'DRIVER'].map(t => (<button key={t} onClick={() => setTargetType(t as any)} className={`px-3 py-1 rounded text-xs border ${targetType === t ? 'bg-f1-red text-white' : 'bg-gray-900 text-gray-500'}`}>{t}</button>))}</div>{targetType === 'TEAM' && <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-sm" onChange={e => setSelectedTeam(e.target.value)}><option>Wähle Team...</option>{teams.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}</select>}{targetType === 'DRIVER' && <div className="max-h-32 overflow-y-auto bg-gray-900 border border-gray-700 rounded p-2 grid grid-cols-2 gap-1 custom-scrollbar">{drivers.map(d=><div key={d.id} onClick={()=>toggleDriver(d.id)} className={`cursor-pointer px-2 py-1 text-xs rounded ${selectedDrivers.includes(d.id)?'bg-green-900 text-green-400':'text-gray-400'}`}>{d.username}</div>)}</div>}</div>
                    <Button className="w-full justify-center" onClick={handleSend} disabled={sending || !msgData.title}>{sending ? <RefreshCw className="animate-spin mr-2"/> : <Send size={16} className="mr-2"/>} Senden</Button>
                </div>
            </Card>
            <Card title="Vorschau" className="bg-[#36393f] border-none">
                <div className="flex gap-3"><div className="w-10 h-10 rounded-full bg-f1-red flex items-center justify-center text-white font-bold">B</div><div className="flex-1"><div className="flex items-baseline gap-2"><span className="font-bold text-white">RaceBot</span><span className="text-[10px] bg-[#5865F2] text-white px-1 rounded">APP</span><span className="text-xs text-gray-400">heute um 12:00</span></div><div className="mt-1 border-l-4 rounded bg-[#2f3136] p-3 max-w-md" style={{borderLeftColor: msgData.color}}><div className="font-bold text-white mb-1">{msgData.title || 'Titel'}</div><div className="text-sm text-gray-300 whitespace-pre-wrap">{msgData.message || 'Nachrichtentext...'}</div>{msgData.url && (<div className="mt-2 text-xs text-blue-400 underline">{msgData.url}</div>)}</div></div></div>
            </Card>
        </div>
    );
};

export const DiscordMonitor: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<'MONITOR' | 'ROUTING' | 'TEMPLATES' | 'CUSTOM' | 'MANUAL'>('MONITOR');
    
    // Monitor State
    const [logs, setLogs] = useState<DiscordLog[]>([]);
    const [forecast, setForecast] = useState<ForecastItem[]>([]);
    const [lastCron, setLastCron] = useState<string | null>(null);
    const [webhookOk, setWebhookOk] = useState(false);
    const [debugUrl, setDebugUrl] = useState('');
    const [eventCount, setEventCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showDebug, setShowDebug] = useState(false);
    
    // Sending
    const [isTriggering, setIsTriggering] = useState(false);

    // Template State
    const [templates, setTemplates] = useState<MessageTemplate[]>([]);
    const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
    const [sendingTemplate, setSendingTemplate] = useState<MessageTemplate | null>(null);

    if (!user || !user.roles.includes(UserRole.WEBADMIN)) {
        return <Navigate to="/" />;
    }

    const loadData = async () => {
        setLoading(true);
        const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
        
        try {
            const res = await fetch(`${baseUrl}/discord_monitor.php`);
            const json = await res.json();
            if (json.status === 'success') {
                setLogs(json.logs || []);
                setForecast(json.forecast || []);
                setLastCron(json.last_cron);
                setWebhookOk(!!json.webhook_configured);
                setDebugUrl(json.debug_url);
                setEventCount(json.event_count || 0);
            }

            const tplRes = await fetch(`${baseUrl}/discord_templates.php`);
            if (tplRes.ok) {
                const tplJson = await tplRes.json();
                if (tplJson.status === 'success') setTemplates(tplJson.data);
            }

        } catch (e: any) { 
            console.error(e);
        } finally { setLoading(false); }
    };

    useEffect(() => { loadData(); }, []);

    const triggerCron = async () => {
        setLoading(true);
        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            const res = await fetch(`${baseUrl}/cron.php`);
            const text = await res.text();
            alert("Cron Output:\n" + text.substring(0, 500));
            await loadData();
        } catch(e: any) { alert("Fehler: " + e.message); }
        finally { setLoading(false); }
    };
    
    // NEW: Force Trigger Logic
    const handleForceTrigger = async (eventId: string, type: string) => {
        const isManual = type === 'MANUAL_SCHEDULED';
        const confirmMsg = isManual 
            ? `Geplante Nachricht jetzt sofort senden?`
            : `Erinnerung für ${type} jetzt sofort senden?`;

        if (!confirm(confirmMsg)) return;
        setIsTriggering(true);
        try {
            const res = await apiRequest<{message: string}>('discord_monitor.php', 'POST', {
                action: 'force_trigger',
                eventId,
                type
            });
            alert(res.message);
            loadData();
        } catch(e: any) {
            alert("Fehler: " + e.message);
        } finally {
            setIsTriggering(false);
        }
    };

    const handleDeleteScheduled = async (id: string) => {
        if (!confirm("Möchtest du diese geplante Nachricht wirklich löschen?")) return;
        try {
            await apiRequest(`discord_custom.php?id=${id}`, 'DELETE');
            loadData();
        } catch(e: any) {
            alert("Fehler: " + e.message);
        }
    };

    const now = new Date();
    const lastRunDate = lastCron ? new Date(lastCron) : null;
    let cronHealth = 'UNKNOWN';
    if (lastRunDate) {
        const diffMins = (now.getTime() - lastRunDate.getTime()) / 60000;
        if (diffMins < 60) cronHealth = 'GOOD';
        else if (diffMins < 180) cronHealth = 'WARN';
        else cronHealth = 'BAD';
    }
    
    // Filter out forecast items that are already processed? No, api only returns pending ones.

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic flex items-center gap-3 text-f1-red">
                        <Activity size={32}/> Discord Center
                    </h1>
                    <p className="text-gray-400 text-sm">Bot Status, Logs & Text-Vorlagen</p>
                </div>
                <div className="flex bg-gray-800 rounded p-1 gap-1 flex-wrap">
                    <button onClick={() => setActiveTab('MONITOR')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeTab==='MONITOR'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}>
                        <Activity size={14}/> Monitor
                    </button>
                    <button onClick={() => setActiveTab('ROUTING')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeTab==='ROUTING'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}>
                        <GitMerge size={14}/> Kanäle / Routing
                    </button>
                    <button onClick={() => setActiveTab('TEMPLATES')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeTab==='TEMPLATES'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}>
                        <FileText size={14}/> Vorlagen
                    </button>
                    <button onClick={() => setActiveTab('MANUAL')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeTab==='MANUAL'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}>
                        <Zap size={14}/> Manuell
                    </button>
                    <button onClick={() => setActiveTab('CUSTOM')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 ${activeTab==='CUSTOM'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}>
                        <Send size={14}/> Custom Sender
                    </button>
                </div>
            </div>

            {activeTab === 'MONITOR' && (
                <>
                    {/* STATUS GRID */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-bold text-gray-400 uppercase text-xs">Cronjob Status</h3>
                                <Activity size={16} className={cronHealth === 'GOOD' ? 'text-green-500' : 'text-red-500'}/>
                            </div>
                            <div className="text-2xl font-black text-white mb-1">
                                {cronHealth === 'GOOD' ? 'ACTIVE' : cronHealth === 'WARN' ? 'DELAYED' : 'INACTIVE'}
                            </div>
                            <div className="text-xs text-gray-500">
                                Letzter Lauf: <span className="text-white font-mono">{lastCron ? new Date(lastCron).toLocaleString() : 'Nie'}</span>
                            </div>
                            <div className="mt-3 flex gap-2">
                                <Button size="sm" variant="secondary" className="w-full justify-center" onClick={triggerCron} disabled={loading} icon={Clock}>Run Now</Button>
                                <Button size="sm" variant="ghost" onClick={loadData} disabled={loading}><RefreshCw size={14}/></Button>
                            </div>
                        </Card>

                        <Card>
                             <div className="flex items-center justify-between mb-4">
                                <h3 className="font-bold text-gray-400 uppercase text-xs">Global Webhook</h3>
                                {webhookOk ? <CheckCircle size={16} className="text-green-500"/> : <AlertTriangle size={16} className="text-red-500"/>}
                            </div>
                            <div className={`text-2xl font-black mb-1 ${webhookOk ? 'text-white' : 'text-red-400'}`}>
                                {webhookOk ? 'Verbunden' : 'Nicht gefunden'}
                            </div>
                            <div className="text-xs text-gray-500">
                                URL: <span className="text-white font-mono" title={debugUrl}>{webhookOk ? 'Konfiguriert (Hidden)' : 'Leer'}</span>
                            </div>
                        </Card>

                        {/* UPCOMING REMINDERS */}
                        <Card className="flex flex-col h-full">
                             <div className="flex items-center justify-between mb-4">
                                <h3 className="font-bold text-gray-400 uppercase text-xs">Anstehende Erinnerungen</h3>
                                <BellRing size={16} className="text-yellow-500"/>
                            </div>
                            <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar max-h-[120px]">
                                {forecast.length === 0 ? <div className="text-gray-500 text-xs italic">Keine Aufgaben.</div> : forecast.map((item, idx) => (
                                    <div key={idx} className={`bg-gray-900 border rounded p-2 flex justify-between items-center gap-2 ${item.type === 'MANUAL_SCHEDULED' ? 'border-f1-red/30' : 'border-gray-800'}`}>
                                        <div className="min-w-0">
                                            <div className="text-xs font-bold text-white truncate flex items-center gap-1">
                                                {item.type === 'MANUAL_SCHEDULED' && <Clock size={10} className="text-f1-red"/>}
                                                {item.event}
                                            </div>
                                            <div className="text-[10px] text-gray-500 font-mono">
                                                {item.type === 'MANUAL_SCHEDULED' ? 'MANUELL' : item.type} • {item.status === 'DUE_NOW' ? <span className="text-red-500 font-bold">FÄLLIG</span> : `${item.hours_until}h`}
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            {item.type === 'MANUAL_SCHEDULED' && (
                                                <button 
                                                    onClick={() => handleDeleteScheduled(item.event_id)}
                                                    className="text-[10px] font-bold bg-gray-800 hover:bg-red-900/50 text-gray-400 hover:text-red-500 p-1 rounded"
                                                    title="Löschen"
                                                >
                                                    <Trash2 size={12}/>
                                                </button>
                                            )}
                                            <button 
                                                onClick={() => handleForceTrigger(item.event_id, item.type)}
                                                disabled={isTriggering}
                                                className="text-[10px] font-bold bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded whitespace-nowrap"
                                            >
                                                {item.type === 'MANUAL_SCHEDULED' ? 'Jetzt' : 'Senden'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>

                    {/* LOGS */}
                    <h2 className="text-xl font-bold text-white uppercase italic mt-8 border-b border-gray-800 pb-2">
                        Nachrichten Log
                    </h2>
                    <div className="bg-[#1e1e24] border border-gray-800 rounded-lg overflow-hidden">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-900 text-gray-400 uppercase text-xs">
                                <tr>
                                    <th className="p-3 w-40">Zeit</th>
                                    <th className="p-3">Titel / Inhalt</th>
                                    <th className="p-3">Ziel</th>
                                    <th className="p-3 text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {logs.map(log => (
                                    <tr key={log.id} className="hover:bg-gray-800/50 transition-colors">
                                        <td className="p-3 font-mono text-gray-500 text-xs">
                                            {new Date(log.created_at).toLocaleString()}
                                        </td>
                                        <td className="p-3">
                                            <div className="font-bold text-white mb-1">{log.title}</div>
                                            <div className="text-xs text-gray-400 truncate max-w-lg">{log.message}</div>
                                        </td>
                                        <td className="p-3 text-xs font-mono text-blue-300 truncate max-w-[150px]" title={log.target}>
                                            {log.target || '-'}
                                        </td>
                                        <td className="p-3 text-right">
                                            <span className={`text-[10px] font-bold px-2 py-1 rounded ${log.status === 'SENT' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
                                                {log.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {activeTab === 'ROUTING' && <ChannelRouting />}

            {activeTab === 'TEMPLATES' && (
                <div className="space-y-6">
                    {/* EDIT MODAL */}
                    {editingTemplate && (
                        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                            <div className="w-full max-w-lg">
                                <TemplateEditor 
                                    template={editingTemplate} 
                                    onCancel={() => setEditingTemplate(null)}
                                    onSave={(newT) => {
                                        setTemplates(prev => prev.map(t => t.id === newT.id ? newT : t));
                                        setEditingTemplate(null);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* SEND MODAL */}
                    {sendingTemplate && (
                        <SendTemplateModal 
                            template={sendingTemplate} 
                            onClose={() => setSendingTemplate(null)}
                        />
                    )}

                    <div className="grid grid-cols-1 gap-4">
                        {templates.map(tpl => (
                            <div key={tpl.id} className="bg-gray-900 border border-gray-700 rounded p-4 flex flex-col md:flex-row justify-between items-start gap-4 hover:border-gray-500 transition-colors">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Badge className="bg-gray-800 text-gray-300 font-mono">{tpl.template_key}</Badge>
                                        <div className="w-4 h-4 rounded-full border border-gray-600 shadow-sm" style={{backgroundColor: tpl.color}}></div>
                                    </div>
                                    <h3 className="text-lg font-bold text-white mb-1">{tpl.title}</h3>
                                    <div className="text-sm text-gray-400 line-clamp-2">{tpl.message}</div>
                                </div>
                                <div className="flex flex-col gap-2 shrink-0">
                                    <Button size="sm" variant="primary" onClick={() => setSendingTemplate(tpl)} className="w-full justify-center">
                                        <Play size={14} className="mr-2"/> Senden / Planen
                                    </Button>
                                    <Button size="sm" variant="secondary" onClick={() => setEditingTemplate(tpl)} icon={Edit2} className="w-full justify-center">
                                        Bearbeiten
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {activeTab === 'MANUAL' && <ManualTrigger />}

            {activeTab === 'CUSTOM' && <CustomMessageBuilder />}
            
            <div className="mt-8 text-center">
                <button onClick={() => setShowDebug(!showDebug)} className="text-xs text-gray-600 hover:text-white underline">
                    {showDebug ? 'Hide Debug Info' : 'Show Debug Info'}
                </button>
                {showDebug && (
                    <div className="mt-4 p-4 bg-gray-900 text-left rounded border border-gray-800 text-xs font-mono text-gray-400 overflow-auto max-h-60">
                        <div className="flex items-center gap-2 text-yellow-500 mb-2"><Terminal size={12}/> DB RAW STATUS</div>
                        <p>Webhook Found: {webhookOk ? 'YES' : 'NO'}</p>
                        <p>Last Cron: {lastCron}</p>
                    </div>
                )}
            </div>
        </div>
    );
};
